import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from 'hooks/useAuth';
import { extractApiError } from 'api/errors';
import { accountUrl } from 'config/hosts';
import { useTokens } from 'theme';
import AuthLayout, { FieldLabel, PasswordToggle } from './AuthLayout';

const loginSchema = z.object({
  email:    z.string().min(1, 'shell.emailRequired').email('shell.validEmail'),
  password: z.string().min(1, 'shell.passwordRequired'),
});

export default function LoginPage() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const { login, completeMfaLogin } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError]   = useState('');
  const [isLoading, setIsLoading]       = useState(false);

  const { register, handleSubmit, resetField, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setServerError('');
    setIsLoading(true);
    try {
      const result = await login({ email: data.email, password: data.password });
      if (result.mfa_required) {
        resetField('password');
        setShowPassword(false);
        setChallenge(result.challenge);
      }
    } catch (err) {
      setServerError(extractApiError(err, translate('shell.loginFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setServerError('');
    setIsLoading(true);
    try { await completeMfaLogin({ challenge, code }); }
    catch (err) { setServerError(extractApiError(err, translate('shell.verifyCodeFailed'))); }
    finally { setIsLoading(false); setCode(''); }
  };

  return (
    <AuthLayout title={translate('shell.welcomeBack')} subtitle={translate('shell.signInSubtitle')}>
      {serverError && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {serverError}
        </Alert>
      )}

      {challenge ? (
        <Box component="form" onSubmit={submitCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          <Typography variant="body2">{translate('shell.enterAuthCode')}</Typography>
          <Box>
            <FieldLabel htmlFor="login-mfa-code">{translate('shell.authRecoveryCode')}</FieldLabel>
            <TextField
              id="login-mfa-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              autoFocus
              required
              fullWidth
              inputProps={{ maxLength: 32 }}
            />
          </Box>
          <Button type="submit" variant="contained" disabled={isLoading || !code}>{translate('shell.verifyAndSignIn')}</Button>
          <Button disabled={isLoading} onClick={() => { setChallenge(null); setCode(''); setServerError(''); }}>{translate('shell.backToSignIn')}</Button>
        </Box>
      ) : <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
      >
        <Box>
          <FieldLabel htmlFor="login-email">{translate('shell.workEmail')}</FieldLabel>
          <TextField
            {...register('email')}
            id="login-email"
            type="email"
            placeholder="you@company.ru"
            fullWidth
            autoComplete="email"
            autoFocus
            error={!!errors.email}
            helperText={errors.email?.message ? translate(errors.email.message) : undefined}
          />
        </Box>

        <Box>
          <FieldLabel
            htmlFor="login-password"
            aside={(
              <Link href={accountUrl('/accounts/password/reset/')} underline="hover" sx={{ fontSize: '0.8rem', fontWeight: 500, color: t.link }}>{translate('shell.forgotPassword')}</Link>
            )}
          >{translate('shell.password')}</FieldLabel>
          <TextField
            {...register('password')}
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            autoComplete="current-password"
            error={!!errors.password}
            helperText={errors.password?.message ? translate(errors.password.message) : undefined}
            InputProps={{
              endAdornment: <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />,
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading}
          endIcon={isLoading ? null : <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
          sx={{ mt: 0.75, minHeight: 46 }}
        >
          {isLoading ? <CircularProgress size={20} sx={{ color: t.onAccent }} /> : translate('shell.signIn')}
        </Button>
      </Box>}

      <Box sx={{ borderTop: `1px solid ${t.border}`, mt: 3.5, pt: 3 }}>
        <Typography sx={{ fontSize: '0.875rem', color: t.inkMuted }}>{translate('shell.newToEvolve')}{' '}
          <Link href={accountUrl('/accounts/signup/')} sx={{ fontWeight: 700, color: t.ink, textDecorationColor: t.ink }}>{translate('shell.createAccount')}</Link>
        </Typography>
      </Box>
    </AuthLayout>
  );
}
